import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { HomeIcon, ClockIcon, UsersIcon, InformationCircleIcon, CalendarIcon } from '@heroicons/react/outline'
import Loader from '../UI/Loader'

const TrendingUpIcon = ({ size = 24, className = '', style = {} }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
		<polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
		<polyline points="17 6 23 6 23 12" />
	</svg>
)

// Pure SVG area chart — no external dependency
const AreaChart = ({ data, width = 800, height = 300 }) => {
	if (!data || data.length === 0)
		return null

	const paddingLeft = 36
	const paddingRight = 16
	const paddingTop = 16
	const paddingBottom = 40
	const chartW = width - paddingLeft - paddingRight
	const chartH = height - paddingTop - paddingBottom

	const maxVal = Math.max(...data.map(d => d.arrivals), 1)
	const yTicks = []
	const step = Math.ceil(maxVal / 4)
	for (let v = 0; v <= maxVal + step; v += step)
		yTicks.push(v)

	const xStep = chartW / (data.length - 1)

	const points = data.map((d, i) => ({
		x: paddingLeft + i * xStep,
		y: paddingTop + chartH - (d.arrivals / (yTicks[yTicks.length - 1] || 1)) * chartH
	}))

	// Build SVG path for line + area
	const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
	const areaPath = linePath
		+ ` L${points[points.length - 1].x.toFixed(1)},${(paddingTop + chartH).toFixed(1)}`
		+ ` L${points[0].x.toFixed(1)},${(paddingTop + chartH).toFixed(1)} Z`

	// Tooltip state is handled outside this component via hover
	return (
		<svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%', overflow: 'visible' }}>
			<defs>
				<linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor="#4f46e5" stopOpacity="0.25" />
					<stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
				</linearGradient>
			</defs>

			{/* Horizontal grid lines */}
			{yTicks.map((v, i) => {
				const y = paddingTop + chartH - (v / (yTicks[yTicks.length - 1] || 1)) * chartH
				return (
					<g key={i}>
						<line x1={paddingLeft} y1={y.toFixed(1)} x2={paddingLeft + chartW} y2={y.toFixed(1)} stroke="#f1f5f9" strokeWidth="1" />
						<text x={paddingLeft - 6} y={y + 4} textAnchor="end" fill="#94a3b8" fontSize="10" fontWeight="700">{v}</text>
					</g>
				)
			})}

			{/* Area fill */}
			<path d={areaPath} fill="url(#areaGrad)" />

			{/* Line */}
			<path d={linePath} fill="none" stroke="#4f46e5" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />

			{/* Dots + X labels */}
			{points.map((p, i) => {
				const showLabel = i % 2 === 0
				return (
					<g key={i}>
						{data[i].arrivals > 0 && (
							<circle cx={p.x} cy={p.y} r="5" fill="#4f46e5" stroke="white" strokeWidth="2" />
						)}
						{showLabel && (
							<text x={p.x} y={paddingTop + chartH + 20} textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="700">
								{data[i].hour}
							</text>
						)}
					</g>
				)
			})}
		</svg>
	)
}

class CheckinsStats extends Component
{
	constructor(props)
	{
		super(props)
		this.state = {
			loading: true,
			stays: []
		}
	}

	componentDidMount()
	{
		if (this.props.updateNavbarTitle)
			this.props.updateNavbarTitle('Pics d\'arrivées')
		this._loadToday()
	}

	_loadToday = () => {
		if (!this.props.fetchArrivals)
		{
			this.setState({ loading: false })
			return ;
		}
		const passed_date = this.props.location && this.props.location.state && this.props.location.state.date
		let dateKey
		if (passed_date)
		{
			dateKey = passed_date
		}
		else
		{
			const now = new Date()
			const day   = String(now.getDate()).padStart(2, '0')
			const month = String(now.getMonth() + 1).padStart(2, '0')
			const year  = String(now.getFullYear())
			dateKey = `${year}-${month}-${day}`
		}
		this._todayKey = dateKey
		this.props.fetchArrivals(this._todayKey)
		.then(data => {
			const list = Array.isArray(data) ? data : []
			this.setState({ loading: false, stays: list })
		})
		.catch(() => {
			this.setState({ loading: false })
		})
	}

	_getArrivalDatestring = (stay) => {
		if (!stay.arrival_time)
			return ''
		return new Date(stay.arrival_time * 1000).toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })
	}

	_getEffectiveArrival = (stay) => {
		const pc = stay.precheckin
		if (!pc)
			return null
		if (pc.estimate_arrival != null)
			return pc.estimate_arrival
		if (pc.estimate_arrival_dashboard != null)
			return pc.estimate_arrival_dashboard
		return null
	}

	_buildHourlyData = (stays) => {
		const hours = []
		for (let h = 7; h <= 23; h++)
			hours.push({ hour: `${String(h).padStart(2, '0')}:00`, arrivals: 0 })
		hours.push({ hour: '00:00', arrivals: 0 })

		for (let i = 0; i < stays.length; i++)
		{
			const ea = this._getEffectiveArrival(stays[i])
			if (ea === null)
				continue
			const hour = Math.floor(ea / 3600)
			if (hour === 0)
				hours[hours.length - 1].arrivals++
			else if (hour >= 7 && hour <= 23)
				hours[hour - 7].arrivals++
		}
		return hours
	}

	_isRealPc = (s) => {
		return !!(s.precheckin && (s.precheckin.firstname || s.precheckin.estimate_arrival != null))
	}

	render()
	{
		const { loading, stays } = this.state
		if (loading)
		{
			return (
				<div className="loader">
					<Loader />
				</div>
			)
		}

		const todayKey = this._todayKey || ''
		const nonCancelled = stays.filter(s => !s.cancelled && this._getArrivalDatestring(s) === todayKey)
		const hourlyData = this._buildHourlyData(nonCancelled)
		const totalArrivals = nonCancelled.length
		const peakHour = [...hourlyData].sort((a, b) => b.arrivals - a.arrivals)[0]
		const withPrecheckin = nonCancelled.filter(s => this._isRealPc(s)).length
		const tauxPrecheckin = totalArrivals > 0 ? Math.round((withPrecheckin / totalArrivals) * 100) : 0

		const dateObj = todayKey ? new Date(todayKey + 'T12:00:00') : new Date()
		const dateLabel = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

		return (
			<div>

					{/* Breadcrumb */}
					<div className="mb-5" aria-label="Breadcrumb">
						<ol className="flex items-center gap-1 text-sm">
							<li className="flex items-center">
								<Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
									<HomeIcon className="flex-shrink-0 h-4 w-4" aria-hidden="true" />
									<span className="sr-only">Accueil</span>
								</Link>
							</li>
							<li className="flex items-center">
								<span className="text-gray-300 mx-1 select-none">/</span>
								<Link to="/checkins/arrivals" className="text-gray-400 hover:text-gray-600 transition-colors">Pré-checkins</Link>
							</li>
							<li className="flex items-center">
								<span className="text-gray-300 mx-1 select-none">/</span>
								<span className="text-gray-600">Pics d'arrivées</span>
							</li>
						</ol>
					</div>

					{/* Title */}
					<div className="flex items-end justify-between mb-8">
						<div>
							<h1 style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '-0.02em' }} className="text-4xl font-bold text-gray-900 leading-none mt-0 mb-0">
								Pics d'arrivées
							</h1>
							<p className="text-sm text-gray-400 mt-2 mb-0 capitalize">{dateLabel}</p>
						</div>
					</div>

					{/* Summary Cards */}
					<div className="grid grid-cols-3 gap-6 mb-8">

						<div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-5">
							<div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
								<ClockIcon className="h-7 w-7" />
							</div>
							<div>
								<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Heure de pointe</p>
								<p className="text-xl font-black text-slate-900">{peakHour && peakHour.arrivals > 0 ? peakHour.hour : '—'}</p>
								<p className="text-xs text-indigo-500 font-bold mt-0.5">
									{peakHour && peakHour.arrivals > 0
										? `${peakHour.arrivals} arrivée${peakHour.arrivals > 1 ? 's' : ''} prévue${peakHour.arrivals > 1 ? 's' : ''}`
										: 'Aucune heure renseignée'}
								</p>
							</div>
						</div>

						<div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-5">
							<div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
								<UsersIcon className="h-7 w-7" />
							</div>
							<div>
								<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total arrivées</p>
								<p className="text-xl font-black text-slate-900">{totalArrivals}</p>
								<p className="text-xs text-emerald-500 font-bold mt-0.5">Sur la journée</p>
							</div>
						</div>

						<div className="p-6 rounded-3xl shadow-sm flex items-center gap-5 relative overflow-hidden" style={{ backgroundColor: '#4f46e5' }}>
							<div className="relative z-10">
								<p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#c7d2fe' }}>Taux de pré-checkin</p>
								<p className="text-xl font-black text-white">{tauxPrecheckin}%</p>
								<p className="text-xs font-medium mt-0.5" style={{ color: '#c7d2fe' }}>
									{withPrecheckin} formulaire{withPrecheckin > 1 ? 's' : ''} reçu{withPrecheckin > 1 ? 's' : ''}
								</p>
							</div>
							<TrendingUpIcon size={80} style={{ position: 'absolute', right: '-1rem', bottom: '-1rem', opacity: 0.1, color: 'white' }} />
						</div>

					</div>

					{/* Chart */}
					<div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
						<div className="flex items-center justify-between mb-8">
							<div>
								<h3 className="text-lg font-black text-slate-900 tracking-tight">Répartition des arrivées par heure</h3>
								<p className="text-xs text-slate-500 font-medium">Répartition des arrivées sur le créneau 07h00 – 00h00</p>
							</div>
							<div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
								<CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
								<span className="text-xs font-bold text-slate-600">Aujourd'hui</span>
							</div>
						</div>

						<div style={{ height: 320 }}>
							<AreaChart data={hourlyData} width={900} height={320} />
						</div>

						{peakHour && peakHour.arrivals > 0 && (
							<div className="mt-8 p-4 bg-slate-50 border border-slate-100 rounded-2xl flex items-start gap-3">
								<InformationCircleIcon className="h-5 w-5 text-indigo-500 mt-0.5 flex-shrink-0" />
								<p className="text-xs text-slate-600 leading-relaxed">
									<span className="font-bold text-slate-900 block mb-1">Conseil d'organisation</span>
									Le pic d'affluence se situe à <span className="font-bold" style={{ color: '#4f46e5' }}>{peakHour.hour}</span> avec {peakHour.arrivals} arrivée{peakHour.arrivals > 1 ? 's' : ''} prévue{peakHour.arrivals > 1 ? 's' : ''}.
									{' '}Pensez à renforcer l'accueil sur cette plage horaire pour fluidifier les entrées.
								</p>
							</div>
						)}
					</div>

			</div>
		)
	}
}

export default CheckinsStats
